/**
 * Este componente es un formulario dinámico que se adapta según la estructura de datos recibida.
 * Se usa para gestionar (crear/editar) condiciones de diferentes "tipos" (promo, send_money, partner, etc.).
 * - Recibe la estructura de formulario (`form_structure`) y el ítem a editar (`item`).
 * - Genera dinámicamente un FormGroup basado en la estructura.
 * - Permite agregar o eliminar ítems (en listas).
 * - Convierte datos entre el formato del formulario y el de DynamoDB (AWS).
 * - Al enviar, construye el payload para DynamoDB y lo envía mediante una llamada Lambda.
 */

import {
  Component, EventEmitter, Input, OnChanges, Output,
  SimpleChanges, inject
} from '@angular/core';
import {
  FormBuilder, FormGroup, FormControl, FormArray,
  Validators, AbstractControl
} from '@angular/forms';
import { AWSLambda } from '../../../AWSLambda.service';
import { TuiDay, TuiTime } from '@taiga-ui/cdk';
import { ConditionTypesService } from '../../../services/condition-types.service';

@Component({
  selector: 'app-conditions-form',
  templateUrl: './conditions-form.component.html',
})
export class ConditionsFormComponent implements OnChanges {
  // Inyección directa del servicio AWSLambda
  lambda = inject(AWSLambda);

  // Servicio de tipos de condición
  conditionTypesService = inject(ConditionTypesService);

  // Entradas del componente
  @Input() form_structure: string = '';  // Tipo de estructura (promo, send_money, etc.)
  @Input() item: any = {};               // Ítem a editar

  // Salida para indicar si el formulario está en modo edición
  @Output() editMode = new EventEmitter<boolean>();

  // Estructura base y datos
  structure: any = {};
  form!: FormGroup;
  finalData: any = {};
  response: any = null;
  appliesToOptions: any[] = [];
  unitOptions: any[] = [];

  constructor(private fb: FormBuilder) { }

  /**
   * Detecta cambios en los inputs y recarga el formulario si es necesario.
   */
  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['form_structure'] && this.form_structure) {
      await this.loadNewStructure();
    }

    if (changes['item'] && this.item && this.form) {
      this.updateFormWithItem(this.item);
    }
  }

  /**
   * Verifica si el nombre de la clave sugiere que es un campo de fecha u hora.
   */
  isDateTimeField(key: string): boolean {
    if (!key) return false;
    const lowerKey = key.toLowerCase();
    return lowerKey.includes('date') || lowerKey.includes('time');
  }

  /**
   * Emite evento para salir del modo edición.
   */
  toggleBlock(event?: Event) {
    if (event) {
      event.preventDefault();
    }
    this.editMode.emit(false);
  }

  /**
   * Carga la estructura por defecto según el tipo de formulario.
   */
  async getDefaultStructure() {
    switch (this.form_structure) {
      case 'promo':
        this.structure = {
          id: { S: '' },
          active: { BOOL: true },
          applyesTo: { S: 'esim' },
          discount: { N: '10' },
          unit: { S: '%' },
          quantity: { N: '5' },
          fromDate: { S: '' },
          toDate: { S: '' },
        };
        break;

      case 'send_money':
        this.structure = {
          id: { S: '' },
          allowCard: { BOOL: true },
          allowFreeCard: { BOOL: false },
          maxCard: { N: '5000' },
          maxFreeCard: { N: '0' },
          allowDB: { BOOL: true },
          allowFreeDB: { BOOL: false },
          maxDB: { N: '5000' },
          maxFreeDB: { N: '0' },
          revenueCard: {
            L: [{
              M: {
                min: { N: '0' },
                max: { N: '9999' },
                quantity: { N: '1.5' },
                tax: { N: '0' },
                unit: { S: '%' },
              },
            }],
          },
          revenueDB: {
            L: [{
              M: {
                min: { N: '0' },
                max: { N: '9999' },
                quantity: { N: '1.5' },
                tax: { N: '0' },
                unit: { S: '%' },
              },
            }],
          },
        };
        break;

      case 'partner':
        this.structure = this.conditionTypesService.getDefaultPartnerStructure();
        // Cargar opciones para el select de appliesTo
        this.appliesToOptions = await this.conditionTypesService.getAppliesToOptions();
        // Cargar opciones para el select de unit
        this.unitOptions = await this.conditionTypesService.getUnitOptions();
        break;
    }
  }

  isNotEmpty(obj: any): boolean {
    return obj && Object.keys(obj).length > 0;
  }

  /**
   * Verifica si un campo es una fecha (heurística).
   */
  isDateField(key: string, value: any): boolean {
    if (typeof value === 'string' && key.toLowerCase().includes('date')) {
      if (value == "") return true;
      const date = Date.parse(value);
      return !isNaN(date);
    }
    return false;
  }

  /**
   * Carga la estructura desde Lambda o la estructura por defecto.
   */
  async loadNewStructure() {
    if (this.item && Object.keys(this.item).length > 0) {
      try {
        const items_data = await this.lambda.getLambdaFunction(
          'lambda',
          '/tools',
          {
            queryStringParameters: {
              method: 'getItems',
              item_type: this.form_structure
            }
          }
        );
        this.structure = items_data.items[0];
      } catch (error) {
        console.error('getting structure from data:', error);
        this.getDefaultStructure();
      }
    } else {
      this.getDefaultStructure();
    }

    this.form = this.createForm(this.structure);
  }

  /**
   * Crea un FormGroup dinámicamente en base a la estructura.
   */
createForm(struct: any): FormGroup {
  const group: any = {};

  Object.keys(struct).forEach((key) => {
    const value = struct[key];

    if (this.isScalar(value)) {
      const isDateTime = this.isDateTimeField(key);
      const isIdField = key === 'id';

      let initialValue = this.extractScalarValue(value);

      // 🔹 Adaptar fecha para TuiDay/TuiTime
      if (this.isDateField(key, initialValue)) {
        const date = initialValue ? new Date(initialValue) : null;
        initialValue = date
          ? [
              new TuiDay(date.getFullYear(), date.getMonth(), date.getDate()),
              new TuiTime(date.getHours(), date.getMinutes()),
            ]
          : null;
      }

      // 🔹 Solo agregamos required si no es fecha, ni unit, ni id
      // appliesTo debe ser requerido para partners
      const validators =
        isDateTime || (key === 'unit') || isIdField ? [] : [Validators.required];

      group[key] = this.fb.group({
        value: [initialValue, validators],
      });

    } else if (Array.isArray(value)) {
      // 🔹 Para arrays reales
      const arr = this.fb.array<FormGroup>([]);
      value.forEach((item: any) => {
        arr.push(this.createSubGroup(item, false));
      });
      group[key] = this.fb.group({ list: arr });

    } else if (value.L !== undefined) {
      // 🔹 Para listas DynamoDB
      const arr = this.fb.array<FormGroup>([]);
      value.L.forEach((item: any) => {
        arr.push(this.createSubGroup(item.M, false));
      });
      group[key] = this.fb.group({ list: arr });
    }
  });

  return this.fb.group(group);
}


  /**
   * Crea un subgrupo (FormGroup) para un ítem de lista.
   */
  createSubGroup(subStruct: any, withValidators = false): FormGroup {
    const group: any = {};
    Object.keys(subStruct).forEach((subKey) => {
      const value = this.extractScalarValue(subStruct[subKey]);
      group[subKey] = new FormControl(value, withValidators ? Validators.required : []);
    });
    return this.fb.group(group);
  }

  /**
   * Convierte un objeto plano a formato DynamoDB.
   */
  toDynamoFormat(obj: any): any {
    const result: any = {};
    Object.keys(obj).forEach(key => {
      const val = obj[key];
      if (typeof val === 'string') {
        result[key] = { S: val };
      } else if (typeof val === 'number') {
        result[key] = { N: val.toString() };
      } else if (typeof val === 'boolean') {
        result[key] = { BOOL: val };
      } else {
        result[key] = val;
      }
    });
    return result;
  }

  /**
   * Actualiza el formulario con los datos de un ítem existente.
   */
  updateFormWithItem(item: any) {
    Object.keys(item).forEach(key => {
      const control = this.form.get(key);

      if (control?.get('list') instanceof FormArray) {
        const list = control.get('list') as FormArray;
        list.clear();
        item[key].forEach((element: any) => {
          const dynamoElement = this.toDynamoFormat(element);
          const subGroup = this.createSubGroup(dynamoElement, false);
          list.push(subGroup);
        });
      } else if (this.isDateTimeField(key)) {
        if (!item[key]) {
          control?.setValue({ value: null });
        } else {
          const date = new Date(item[key]);
          control?.setValue({
            value: [
              new TuiDay(date.getFullYear(), date.getMonth(), date.getDate()),
              new TuiTime(date.getHours(), date.getMinutes()),
            ],
          });
        }
      } else if (control) {
        control.setValue({ value: item[key] });
      }
    });
  }

  /**
   * Extrae el valor real de un campo DynamoDB.
   */
  extractScalarValue(obj: any): any {
    if (obj.S !== undefined) return obj.S;
    if (obj.N !== undefined) return Number(obj.N);
    if (obj.BOOL !== undefined) return obj.BOOL;
    return '';
  }

  /**
   * Verifica si un objeto es un valor escalar DynamoDB.
   */
  isScalar(obj: any): boolean {
    return obj.S !== undefined || obj.N !== undefined || obj.BOOL !== undefined;
  }

  /**
   * Agrega un nuevo ítem a una lista.
   */
  addItem(key: string) {
    const subStruct = this.structure[key].L[0].M;
    const newItem = this.createSubGroup(subStruct, false);
    const list = this.form.get(key)?.get('list') as FormArray;

    this.form.disable({ emitEvent: false });
    list.push(newItem);
    this.form.enable({ emitEvent: false });
  }

  /**
   * Elimina un ítem de una lista.
   */
  removeItem(key: string, index: number) {
    (this.form.get(key)?.get('list') as FormArray).removeAt(index);
  }

  /**
   * Helpers para verificar tipos.
   */
  isBoolean(value: any): boolean {
    return typeof value === 'boolean';
  }

  getFormArray(control: AbstractControl | null): FormArray {
    return control as FormArray;
  }

  /**
   * Envía el formulario al backend (Lambda).
   */
  async submit() {
    this.form.markAllAsTouched();
    console.log(Object)
    let isValid = true;
    Object.keys(this.structure).forEach((key) => {
      
      if (this.structure[key].L !== undefined) {
        console.log("primer if",this.structure[key].L)
        const list = this.form.get(key)?.get('list') as FormArray;
        for (let group of list.controls) {
          for (let controlName in (group as FormGroup).controls) {
            const control = (group as FormGroup).get(controlName);
              console.log("validador",control?.hasValidator(Validators.required))
              // console.log("valor ",control.value )
              // console.log("condicion",(control.value === null || control.value === ''))
            if (control?.hasValidator(Validators.required) && (control.value === null || control.value === '')) {
              console.log("2do if",(control?.hasValidator(Validators.required) && (control.value === null || control.value === '')))

              console.log("validador",control?.hasValidator(Validators.required))
              console.log("valor ",control.value )
              console.log("condicion",(control.value === null || control.value === ''))
              isValid = false;
              control.setErrors({ required: true });
            }
          }
        }
      } else {
        const control = this.form.get(key)?.get('value');
        if (control?.hasValidator(Validators.required) && (control.value === null || control.value === '')) {
          console.log("3er if",this.structure[key].L)
          isValid = false;
          control.setErrors({ required: true });
        }
      }
    });

    if (!isValid) {
      console.log("entra en el invalido")
      return;
    }

    // Construir el payload final (conversión de fechas, arrays, etc.)
    const raw = this.form.value;
    const payload: any = {};

    console.log("continua")

    Object.keys(this.structure).forEach((key) => {
      if (this.structure[key].S !== undefined) {
        let val = raw[key].value;

        if (key.toLowerCase().includes('date')) {
          if (val && val[0]) {
            const day = val[0];
            const time = val[1];
            const hour = (time != null ? time.hours : key.toLowerCase().includes('to') ? 23 : 0);
            const minute = (time != null ? time.minutes : key.toLowerCase().includes('to') ? 59 : 0);
            const date = new Date(day.year, day.month, day.day, hour, minute);
            val = date.toISOString();
          } else {
            val = ''
          }
        }

        payload[key] = { S: val };
      } else if (this.structure[key].N !== undefined) {
        payload[key] = { N: raw[key].value.toString() };
      } else if (this.structure[key].BOOL !== undefined) {
        payload[key] = { BOOL: raw[key].value };
      } else if (this.structure[key].L !== undefined) {
        payload[key] = {
          L: raw[key].list.map((item: any) => ({
            M: Object.keys(item).reduce((acc: any, subKey) => {
              const originalType = this.structure[key].L[0].M[subKey];
              let val = item[subKey];
              if (originalType.S !== undefined) {
                acc[subKey] = { S: val };
              } else if (originalType.N !== undefined) {
                acc[subKey] = { N: val.toString() };
              } else if (originalType.BOOL !== undefined) {
                acc[subKey] = { BOOL: val };
              }
              return acc;
            }, {}),
          })),
        };
      }
    });

    this.finalData = payload;

    // 👇 Si no hay id => es nuevo
    const method = !payload.id || !payload.id.S ? "newItem" : "upsertItem";
    const params = {
      method,
      item_type: this.form_structure,
      item: JSON.stringify(this.finalData)
    };

    console.log("info a enviar",params)

    // Llamada a Lambda para guardar el ítem
    await this.lambda.getLambdaFunction(
      'lambda','/tools',{
        queryStringParameters: params   // 👈 aquí directo, no anidado
      }
    );

    // Recargar la página después de guardar
    window.location.reload();
  }

  /**
   * Devuelve las claves de un objeto.
   */
  keys(obj: any): string[] {
    return Object.keys(obj);
  }

  /**
   * Verifica si el partnerCode viene de la URL (preestablecido)
   */
  isPartnerCodeFromUrl(): boolean {
    return this.form_structure === 'partner' && 
           this.item && 
           this.item.partnerCode && 
           this.item.partnerCode !== '';
  }

  /**
   * Obtiene los campos en el orden específico para partners
   */
  getOrderedFields(itemValue: any): string[] {
    if (this.form_structure === 'partner') {
      return ['min', 'max', 'unit', 'quantity'];
    }
    return this.keys(itemValue);
  }
}
